// src/containers/TouristicSites/TouristicSite/TouristicSiteV3Page.jsx
import React, {useContext, useEffect} from 'react';
import {useParams, Redirect} from 'react-router-dom';
import {Helmet} from 'react-helmet';
import styled, {ThemeProvider} from 'styled-components';
import {useTranslation} from 'react-i18next';
import v3Theme from '../../../_ui/_v3/theme';
import {PageStyled, Wrap} from '../../../_ui/_v3/primitives';
import NavBarContext from '../../../_contexts/NavBarProvider';
import useTouristicSiteDetailsV3 from '../../../hooks/touristic_sites/useTouristicSiteDetailsV3';
import useTouristicSiteTranslation from '../../../hooks/touristic_sites/useTouristicSiteTranslation';
import buildSections from '../../../utils/touristic_sites/_v3/buildSections';
import mainImage from '../../../utils/touristic_sites/mainImage';
import SiteHeader from '../../../components/_v3/touristic-site/SiteHeader';
import SiteFooter from '../../../components/_v3/touristic-site/SiteFooter';
import Hero from '../../../components/_v3/touristic-site/Hero';
import AnchorSubNav from '../../../components/_v3/touristic-site/AnchorSubNav';
import InfoSection from '../../../components/_v3/touristic-site/InfoSection';
import PricesSection from '../../../components/_v3/touristic-site/PricesSection';
import PracticalSection from '../../../components/_v3/touristic-site/PracticalSection';
import MediasSection from '../../../components/_v3/touristic-site/MediasSection';
import ReviewsSection from '../../../components/_v3/touristic-site/ReviewsSection';
import NearbyRail from '../../../components/_v3/touristic-site/NearbyRail';
import PageSkeleton, {ContentFadeIn} from '../../../components/_v3/touristic-site/PageSkeleton';
import AppPromoBanner from '../../../components/_v3/touristic-site/AppPromoBanner';
import promoPortrait from 'assets/img/promo/app-promo-portrait.jpeg';
import promoLandscape from 'assets/img/promo/app-promo-landscape.jpeg';

const SectionStyled = styled.section`
  scroll-margin-top: 120px; /* header 64px + subnav ~48px */
`;

// Grille "contenu principal + colonne pub sticky" (maquette : side-rail desktop, slot mobile)
const PageShell = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 300px;
  gap: 2rem;
  align-items: start;

  @media (max-width: 1000px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const MainCol = styled.div`
  min-width: 0;
`;

const SideRail = styled.aside`
  position: sticky;
  top: 84px;

  @media (max-width: 1000px) {
    display: none;
  }
`;

const MobileAdSlot = styled.div`
  display: none;
  margin: 1.2rem 0 1.6rem;

  @media (max-width: 1000px) {
    display: block;
  }
`;

const SECTION_COMPONENTS = {
  infos: InfoSection,
  tarifs: PricesSection,
  pratique: PracticalSection,
  medias: MediasSection,
  avis: ReviewsSection,
};

const TouristicSiteV3Page = () => {
  const {touristic_site_id} = useParams();
  const {i18n} = useTranslation(['touristic-site']);
  const {site, nearby, notFound} = useTouristicSiteDetailsV3(touristic_site_id);
  const {title, description} = useTouristicSiteTranslation(site || {});
  const navBarContext = useContext(NavBarContext);

  useEffect(() => {
    const prevBottomBar = navBarContext.bottomBarAppearOnScroll();
    window.scrollTo(0, 0);
    navBarContext.setNavBar('without');
    navBarContext.setFooterVisible(false);
    navBarContext.setBottomBarAppearOnScroll(false);
    return () => {
      navBarContext.restoreNavBar();
      navBarContext.restoreFooterVisible();
      navBarContext.setBottomBarAppearOnScroll(prevBottomBar);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [touristic_site_id]);

  if (notFound) {
    return <Redirect to="/error/404" />;
  }

  const sections = site ? buildSections({site, nearby, lang: i18n.language}) : [];
  const ogImage = site && mainImage(site);

  return (
    <ThemeProvider theme={v3Theme}>
      <PageStyled>
        <Helmet>
          <title>{title ? `${title} | Henri Trip` : 'Henri Trip'}</title>
          {description && <meta name="description" content={description.slice(0, 160)} />}
          {title && <meta property="og:title" content={title} />}
          {ogImage && ogImage.url && <meta property="og:image" content={ogImage.url} />}
        </Helmet>
        <SiteHeader />
        {site && site.id ? (
          <ContentFadeIn>
            <Wrap>
              <PageShell>
                <MainCol>
                  <Hero site={site} />
                  <MobileAdSlot>
                    <AppPromoBanner image={promoLandscape} />
                  </MobileAdSlot>
                  <AnchorSubNav sections={sections} />
                  <main>
                    {sections.map(({id}) => {
                      if (id === 'proximite') {
                        return (
                          <SectionStyled key={id} id={id}>
                            <NearbyRail sites={nearby} />
                          </SectionStyled>
                        );
                      }
                      const Section = SECTION_COMPONENTS[id];
                      return (
                        <SectionStyled key={id} id={id}>
                          <Section site={site} />
                        </SectionStyled>
                      );
                    })}
                  </main>
                </MainCol>
                <SideRail>
                  <AppPromoBanner image={promoPortrait} />
                </SideRail>
              </PageShell>
            </Wrap>
          </ContentFadeIn>
        ) : (
          <Wrap>
            <PageSkeleton />
          </Wrap>
        )}
        <SiteFooter />
      </PageStyled>
    </ThemeProvider>
  );
};

export default TouristicSiteV3Page;
